var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import useVisitPatch from '@hooks/requests/visit-patch-hook';
import useVisitProcessor from '@hooks/processors/use-visit-processor';
import EditGenericEventModal from '@features/Modals/EditGenericEventModal';
import { EntityTypePrefix } from '@features/Calendar/constants';
import { TimeZoneContext } from '@contexts/timezone-context';
import TimeOffModal from '../../Modals/TimeOffModal';
import VisitEditModal from '../../Modals/VisitModal';
import DevSettingsModal from '../DevSettingsModal';
import shouldScheduleOnAssignment from './shouldScheduleOnAssignment';
const defaultConfig = {
    setVisitToScheduledOnAssignment: true,
    preserveDaySpan: true
};
export const ScheduleAndDispatchContext = createContext(undefined);
export function ScheduleAndDispatchProvider(params) {
    const { timezone: schedulerTimezone, timeZonesMapping } = useContext(TimeZoneContext);
    const [activeModal, setActiveModal] = useState(null);
    const [modifiedEventWatch, setModifiedEventWatch] = useState(null);
    const [modifiedEventsWatch, setModifiedEventsWatch] = useState(null);
    const [deletedEventsWatch, setDeletedEventsWatch] = useState(null);
    const [activeVisit, setActiveVisit] = useState(null);
    const configuration = useRef(defaultConfig);
    const { save: saveVisit, result: savedVisit } = useVisitPatch();
    const { processVisit } = useVisitProcessor();
    useEffect(() => {
        if (savedVisit)
            setModifiedEventWatch(processVisit(savedVisit));
    }, [savedVisit]);
    const openTimeOffModal = useCallback((id) => {
        setActiveModal(React.createElement(TimeOffModal, { id: id, onClose: () => setActiveModal(null) }));
    }, []);
    const openEditGenericEventModal = useCallback((id) => {
        setActiveModal(React.createElement(EditGenericEventModal, { id: id, onClose: () => {
                setActiveModal(null);
            }, onDelete: (genericEvents) => {
                setActiveModal(null);
                setDeletedEventsWatch(genericEvents.map((eventId) => `${EntityTypePrefix.Generic_event}-${eventId}`));
            }, onSave: (genericEvents) => {
                setModifiedEventsWatch(genericEvents);
            } }));
    }, []);
    const openVisitModal = useCallback((id) => {
        setActiveVisit(id);
        setActiveModal(React.createElement(VisitEditModal, { id: id, onClose: () => {
                setActiveVisit(null);
                setActiveModal(null);
            }, onSave: (updatedVisit) => {
                setActiveVisit(null);
                setActiveModal(null);
                setModifiedEventWatch(updatedVisit);
            } }));
    }, []);
    const openExperimentalModal = useCallback(() => {
        setActiveModal(React.createElement(DevSettingsModal, { onClose: () => {
                setActiveModal(null);
            } }));
    }, []);
    const setVisitScheduledOnAssignment = useCallback((setScheduled) => {
        configuration.current = Object.assign(Object.assign({}, configuration.current), { setVisitToScheduledOnAssignment: setScheduled });
    }, []);
    const setPreserveDaySpan = useCallback((preserveDaySpan) => {
        configuration.current = Object.assign(Object.assign({}, configuration.current), { preserveDaySpan });
    }, []);
    const saveVisitOverride = useCallback((visit, originalState) => __awaiter(this, void 0, void 0, function* () {
        // Convert visit times from scheduler timezone to visit's timezone
        // During dragging, times are in scheduler timezone for visual consistency
        // On save, we need to convert to visit's timezone to preserve the visual position
        let updatedVisit = Object.assign({}, visit);
        const visitTimeZone = visit.timeZone;
        if (schedulerTimezone !== visitTimeZone) {
            // Convert human-readable timezone to IANA identifier (e.g., "Central Time (US & Canada)" -> "America/Chicago")
            const visitIanaTimezone = timeZonesMapping[visitTimeZone] || visitTimeZone;
            // Convert start and end times from scheduler timezone to visit timezone without mutating original
            updatedVisit = Object.assign(Object.assign({}, updatedVisit), { start: visit.start.tz(visitIanaTimezone), end: visit.end.tz(visitIanaTimezone) });
        }
        if (configuration.current.preserveDaySpan && updatedVisit.daySpan > 0) {
            const carryOverMinutes = updatedVisit.durationMins % 1440;
            const newEnd = updatedVisit.start
                .startOf('day')
                .add(updatedVisit.daySpan, 'days')
                .add(carryOverMinutes, 'minutes');
            updatedVisit = Object.assign(Object.assign({}, updatedVisit), { end: newEnd, durationMins: newEnd.diff(updatedVisit.start.startOf('day'), 'minutes') });
        }
        else {
            const visitStartMinutes = updatedVisit.start.minute() + updatedVisit.start.hour() * 60;
            const visitEndMinutes = updatedVisit.end.minute() + updatedVisit.end.hour() * 60;
            updatedVisit = Object.assign(Object.assign({}, updatedVisit), { durationMins: visitEndMinutes - visitStartMinutes });
        }
        if (shouldScheduleOnAssignment(configuration.current.setVisitToScheduledOnAssignment, updatedVisit, originalState)) {
            yield saveVisit(Object.assign(Object.assign({}, updatedVisit), { status: Object.assign(Object.assign({}, updatedVisit.status), { key: 'scheduled' }) }));
        }
        else {
            yield saveVisit(updatedVisit);
        }
    }), [schedulerTimezone, timeZonesMapping, saveVisit]);
    // expose experimental modal to the window for console execution
    useEffect(() => {
        window.openExperimentalModal = openExperimentalModal;
        return () => {
            delete window.openExperimentalModal;
        };
    }, [openExperimentalModal]);
    const contextData = useMemo(() => {
        const value = {
            activeVisit,
            openVisitModal,
            openTimeOffModal,
            openEditGenericEventModal,
            openExperimentalModal,
            activeModal,
            modifiedEventWatch,
            deletedEventsWatch,
            modifiedEventsWatch,
            saveVisit: saveVisitOverride,
            setVisitScheduledOnAssignment,
            setPreserveDaySpan
        };
        return value;
    }, [activeModal, modifiedEventWatch, modifiedEventsWatch, deletedEventsWatch, activeVisit]);
    return (React.createElement(ScheduleAndDispatchContext.Provider, { value: contextData }, params.children));
}
